import { ManifoldWindow } from 'build/mount';
import { inject, provide } from 'vue';
import { mixins, Options, Vue } from 'vue-class-component';
import { Store } from 'vuex';
import { EthereumNetwork } from '@manifoldxyz/manifold-sdk';
import { CAMPAIGN_STORE } from '@/constants/campaign';
import store from '@/store';

/*
 * @dev:
 *
 * Adding a new prop? Declare it in two places:
 *   1. MWidgetOptionsProps with type and default
 *   2. MWidgetProps (theres an obvious pattern)
 *
 * Optionally, enforce any new prop-dependency rules
 * in the created() hook of MWidgetProvidePropsMixin.
 */
export const MCampaignWidgetOptionsProps = {
  // Come from BaseClass -- base.ts
  campaignId: {
    type: Number,
    required: true,
    default: undefined
  },
  clientId: {
    type: String,
    default: ''
  },
  network: {
    type: Number,
    default: EthereumNetwork.MAINNET
  },
  appName: {
    type: String,
    default: ''
  },
  grantType: {
    type: String,
    // type: OAuthGrantType,
    default: 'signature'
  },
  innerClaimText: {
    type: String,
    default: ''
  },
  redemptionSuccessText: {
    type: String,
    default: ''
  },
  eventName: {
    type: String,
    default: ''
  },
  eventDescription: {
    type: String,
    default: ''
  },
  emailSubjectLine: {
    type: String,
    default: ''
  },
  emailBody: {
    type: String,
    default: ''
  },
  eventBaseURL: {
    type: String,
    default: ''
  },
  eventConfirmation: {
    type: String,
    default: ''
  },
  ticketActionList: {
    type: Array,
    default: ['none'] // trying this instead of []
  },
  dataEventName: {
    type: String,
    default: 'This is your event title'
  },
  dataEventDescription: {
    type: String,
    default: 'This is your event description'
  },
  fallbackProvider: {
    type: String,
    default: undefined,
    required: false
  },
  // Come from Campaign.vue
  claimText: {
    type: String,
    default: 'Claim'
  },
  claimButtonImage: {
    type: String,
    default: ''
  },
  connectWalletText: {
    type: String,
    default: 'Connect Wallet'
  }
};

export type MCampaignWidgetOptionsPropsType = keyof typeof MCampaignWidgetOptionsProps;

/* @dev: Want to use an Object as a prop? Use this example pattern: */
export type ExampleType = 'something';
export class ExampleObjectProp {
  // add as many properties as you want here
  type?: ExampleType;

  // do not change this at all
  constructor(object: unknown) {
    Object.assign(this, object);
  }
}

/*
 * Defined once and extended so that the inject() and provide() calls made by
 * the two mixins in this file define all of the same things.
 */
export class MCampaignWidgetProps extends Vue {
  // follow the pattern below when adding new props
  clientId = MCampaignWidgetOptionsProps.clientId.default;
  campaignId = MCampaignWidgetOptionsProps.campaignId.default;
  network = MCampaignWidgetOptionsProps.network.default;
  appName = MCampaignWidgetOptionsProps.appName.default;
  grantType = MCampaignWidgetOptionsProps.grantType.default;
  innerClaimText = MCampaignWidgetOptionsProps.innerClaimText.default;
  redemptionSuccessText = MCampaignWidgetOptionsProps.redemptionSuccessText.default;
  eventName = MCampaignWidgetOptionsProps.eventName.default;
  eventDescription = MCampaignWidgetOptionsProps.eventDescription.default;
  emailSubjectLine = MCampaignWidgetOptionsProps.emailSubjectLine.default;
  emailBody = MCampaignWidgetOptionsProps.emailBody.default;
  eventBaseURL = MCampaignWidgetOptionsProps.eventBaseURL.default;
  eventConfirmation = MCampaignWidgetOptionsProps.eventConfirmation.default;
  ticketActionList = MCampaignWidgetOptionsProps.ticketActionList.default;
  dataEventName = MCampaignWidgetOptionsProps.dataEventName.default;
  dataEventDescription = MCampaignWidgetOptionsProps.dataEventDescription.default;
  fallbackProvider = MCampaignWidgetOptionsProps.fallbackProvider.default;
  claimText = MCampaignWidgetOptionsProps.claimText.default;
  claimButtonImage = MCampaignWidgetOptionsProps.claimButtonImage.default;
  connectWalletText = MCampaignWidgetOptionsProps.connectWalletText.default;
}

/*
 * Used by WalletMixin to inject() back in all parent-provided props as variables
 * Reference: https://vuejs.org/guide/components/provide-inject.html
 */
export class MCampaignWidgetInjectContext extends mixins(MCampaignWidgetProps) {
  $store: Store<any>;
  mmid = '';

  created(): void {
    // get all property definitions declared in MCampaignProvidePropsMixin
    const keys = Object.keys(new MCampaignWidgetProps({}));
    for (let i = 0; i < keys.length; i++) {
      const key = keys[i];
      (this as any)[key] = inject(key); // eslint-disable-line
    }

    // get additional properties
    this.mmid = inject('mmid') || '';
    this.$store = inject('$store') || (window as Window as ManifoldWindow)[CAMPAIGN_STORE];
  }
}

const CampaignPropsSavedToStore = [
  // Note: Are these even saved in store in Vue2?
  'innerClaimText',
  'redemptionSuccessText',
  'eventName',
  'eventDescription',
  'emailSubjectLine',
  'emailBody',
  'eventBaseURL',
  'eventConfirmation',
  'ticketActionList',
  'dataEventName',
  'dataEventDescription',
  'claimText',
  'claimButtonImage',
  'connectWalletText'
] as const;

/**
 * Props that might take a computed default value set in MCampaignPropProvider.created().
 * Mapping of Vue prop name to MCampaignPropProvider internal property name.
 *
 * @dev: add the corresponding property to MCampaignPropProvider and the computed logic
 * to MCampaignPropProvider.created().
 */
const OverriddenProps = {
  // Examples from marketplace: -- Not sure if there are any here?
  // maybe redemption token id?
  // version: 'marketplaceVersion',
  // address: 'contractAddress',
  // referralAddress: 'referrer',
} as const;

type OverriddenPropKey = keyof typeof OverriddenProps;

function isOverriddenPropKey(key: string): key is OverriddenPropKey {
  return key in OverriddenProps;
}

/*
 * Used by MWidget.vue to provide() all its props to children components
 * Reference: https://vuejs.org/guide/components/provide-inject.html
 */
@Options({
  props: MCampaignWidgetOptionsProps
})
export class MCampaignWidgetPropProvider extends mixins(MCampaignWidgetProps) {
  popupIsOpen = false;
  htmlOverflowInitial: string | undefined;
  bodyOverflowInitial: string | undefined;
  mmid = `${this.campaignId}`;

  /*
   * Use this created hook to enforce any prop-dependency rules.
   */
  created(): void {
    if (!this.campaignId) {
      throw new Error("Campaign: 'campaignId' prop is required");
    }
    if (![EthereumNetwork.MAINNET, EthereumNetwork.GOERLI].includes(this.network)) {
      throw new Error(`Unsupported network "${this.network.toString()}".`);
    }
    if ((this.appName || this.clientId) && !(this.appName && this.clientId)) {
      throw Error('Props app-name and client-id must be provided together');
    }
    // if (this.detectApp && (this.appName || this.clientId)) {
    //   throw Error('Props app-name and client-id cannot be provided if detect-app is true');
    // }
    if (this.fallbackProvider && !this.network) {
      throw Error('Prop network must be provided if fallbackProvider is provided');
    }

    // Set this.mmid to just be the campaignId as a string.
    // The new widget patterns use the idea of an mmid (a unique id for data the widget is based on i.e. campaign or marketplace listing)
    this.mmid = `${this.campaignId}`;

    // If there is no store, register a store and set the campaignId.
    if (!this.$store.state[this.mmid]) {
      this.$store.registerModule(`${this.mmid}`, store);
      this.$store.commit(`${this.mmid}/setCampaignId`, this.mmid, { root: true });
    }

    // Take all the additional props potentially passed into the widget
    // and set those corresponding values in the store.
    this.saveProvidedPropsToStore();
    this.provideProps();

    // ~~ from og vue2 createad, not sure if needed
    this.htmlOverflowInitial = document.documentElement.style.overflowY;
    this.bodyOverflowInitial = document.body.style.overflowY;
    document.documentElement.style.overflowY = 'hidden !important';
    document.body.style.overflowY = 'hidden !important';
  }

  // Private getter for $store -- taken from Jaxon marketplace widget to continue using this.$store everywhere
  get $store(): Store<any> {
    const store = (window as Window as ManifoldWindow)[CAMPAIGN_STORE];
    if (!store) {
      // shouldn't happen...
      throw new Error('Marketplace: No store found.');
    }
    return store;
  }

  private saveProvidedPropsToStore(): void {
    CampaignPropsSavedToStore.forEach((propName) => {
      this.$store.commit(
        `${this.mmid}/set${propName.charAt(0).toUpperCase()}${propName.substring(1)}`,
        this.getPropValue(propName),
        { root: true }
      );
    });
  }

  /**
   * This function sets the props in a look up table for the children components to get from if needed.
   * This follows the provide()/inject() pattern in Vue -- otherwise known as prop "drilling" so we
   * don't have to pass props down multiple levels.
   */
  private provideProps(): void {
    // define all properties for children who use the MMarketplaceInjectContext mixin
    const keys = Object.keys(new MCampaignWidgetProps({}));
    for (let i = 0; i < keys.length; i++) {
      const key = keys[i];
      provide(key, this.getPropValue(key));
    }

    // Provide the mmid and $store to children components.
    // (Store them in a lookup table so children classes/components can access them.)
    provide('mmid', `this.campaignId`);
    provide('$store', this.$store);
  }

  private getPropValue(propName: string): any {
    let value = (this as any)[propName];
    if (isOverriddenPropKey(propName)) {
      value = this[OverriddenProps[propName]];
    }
    return value;
  }
}
